// Client 360 tab-content blocks — matches the FTPIG demo exactly,
// tab-by-tab. Each block is self-contained and takes the same
// shared props from HealthClientProfileModal.
//
// Exports:
//   OverviewInsuranceBlock  — Insurance Summary section
//   OverviewAddressBlock    — Address + Last contact
//   MedicalTabView          — Providers + Prescriptions + Conditions
//   HouseholdCoverageMap    — Per-person coverage checkmarks
//   RelationshipTabView     — Personal memory + Who they are
//   ComplianceChecklistBlock — 7-item ready-to-submit checklist
//   DocumentChecklistBlock  — 4-item document checklist
//   TimelineBlock           — Dated event list (demo style)
//   CrossSellOpportunitiesBlock — $ /yr gap list with Create task

import React from 'react';
import { readPersistedJwtDirect, authedFetch } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

/* Products the household coverage map + cross-sell block score against */
const PRODUCTS = [
  { key: 'health',   label: 'Health' },
  { key: 'dental',   label: 'Dental',           product: 'DentalWise Plan 1000',      annual: 420 },
  { key: 'vision',   label: 'Vision',           product: 'DentalWise Max (adds vision)', annual: 144 },
  { key: 'life',     label: 'Life / Final Expense', product: 'Term / Final Expense',   annual: 540 },
  { key: 'accident', label: 'Accident',         product: 'AccidentWise GI',           annual: 180 },
  { key: 'cancer',   label: 'Cancer',           product: 'CriticalGuard — Cancer',    annual: 240 },
  { key: 'hospital', label: 'Hospital Indemnity', product: 'HospitalWise Bundle 2',   annual: 660 },
  { key: 'critical', label: 'Critical Illness', product: 'CriticalGuard — CI',        annual: 300 },
];

function hasProduct(policies, key) {
  const matcher = key === 'life' ? /life|final|term/i
                : key === 'accident' ? /accident/i
                : key === 'cancer' ? /cancer/i
                : key === 'critical' ? /critical/i
                : key === 'hospital' ? /hospital|indemnity/i
                : new RegExp(key, 'i');
  return (policies || []).some(p => p.status === 'active' && matcher.test(String(p.plan_type || p.plan_name || '')));
}

function Label({ children }) {
  return <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">{children}</div>;
}
function Value({ children }) {
  return <div className="text-[14px] text-slate-900 mt-1">{children || <span className="text-slate-400 italic">—</span>}</div>;
}
function Cell({ label, value }) {
  return <div><Label>{label}</Label><Value>{value}</Value></div>;
}

/* ─────────────────────────────────────────────────────────────
   OVERVIEW · Insurance summary
   ───────────────────────────────────────────────────────────── */
export function OverviewInsuranceBlock({ primary }) {
  const fmt$ = (n) => n == null ? null : `$${Math.round(Number(n)).toLocaleString()}`;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <Label>Insurance summary</Label>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        <Cell label="Carrier"          value={primary?.carrier}/>
        <Cell label="Plan"             value={primary?.plan_name}/>
        <Cell label="Metal level"      value={primary?.metal || primary?.plan_type}/>
        <Cell label="Effective"        value={primary?.effective_date ? new Date(primary.effective_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null}/>
        <Cell label="Renewal"          value={primary?.renewal_date ? new Date(primary.renewal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null}/>
        <Cell label="Premium"          value={primary?.premium_monthly_cents ? `$${Math.round(primary.premium_monthly_cents / 100)}/mo` : primary?.monthly_premium ? `${fmt$(primary.monthly_premium)}/mo` : null}/>
        <Cell label="APTC subsidy"     value={primary?.aptc_monthly_cents ? `$${Math.round(primary.aptc_monthly_cents / 100)}/mo` : primary?.aptc_amount ? `${fmt$(primary.aptc_amount)}/mo` : null}/>
        <Cell label="Deductible"       value={fmt$(primary?.deductible)}/>
        <Cell label="Max out-of-pocket" value={fmt$(primary?.max_out_of_pocket || primary?.moop)}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   OVERVIEW · Address + Last contact
   ───────────────────────────────────────────────────────────── */
export function OverviewAddressBlock({ primary, clientId, ownerId }) {
  const [lastContact, setLastContact] = React.useState(null);
  React.useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('messages').select('sent_at').eq('lead_id', String(clientId)).order('sent_at', { ascending: false }).limit(1).maybeSingle();
        if (!cancelled && data?.sent_at) {
          const days = Math.round((Date.now() - new Date(data.sent_at).getTime()) / 86_400_000);
          setLastContact(days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [clientId]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <Label>Location &amp; contact recency</Label>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        <Cell label="Address" value={primary?.client_address}/>
        <Cell label="County"  value={primary?.client_county ? `${primary.client_county} County` : null}/>
        <Cell label="Last contact" value={lastContact}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MEDICAL · Providers · Prescriptions · Conditions
   ───────────────────────────────────────────────────────────── */
export function MedicalTabView({ clientId, ownerId }) {
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!clientId || !ownerId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('client_medical_profile').select('*').eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle();
        if (!cancelled) setProfile(data || null);
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  if (loading) return <div className="text-xs text-slate-500 italic px-1 py-2">Loading medical profile…</div>;

  const specialists = Array.isArray(profile?.specialists) ? profile.specialists : [];
  const rx = Array.isArray(profile?.current_prescriptions) ? profile.current_prescriptions : [];
  const conds = Array.isArray(profile?.medical_conditions) ? profile.medical_conditions : [];

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between">
          <Label>Providers</Label>
          <div className="text-[10.5px] text-slate-400 italic">
            AI cross-checks each provider against the plan's network before renewal.
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
          <Cell label="Primary doctor"    value={profile?.primary_doctor}/>
          <Cell label="Preferred hospital" value={profile?.preferred_hospital}/>
          <Cell label="Pharmacy"          value={profile?.preferred_pharmacy}/>
        </div>
        <div className="mt-4">
          <Label>Specialists</Label>
          <div className="mt-1 text-[13.5px] text-slate-900">
            {specialists.length === 0
              ? <span className="text-slate-400 italic">—</span>
              : specialists.map((s, i) => (
                  <span key={i} className="inline-block mr-3">{s.name || '—'}{s.type ? ` · ${s.type}` : ''}</span>
                ))}
          </div>
        </div>
        <ProviderNetworkStatus profile={profile}/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between">
          <Label>Prescriptions</Label>
          <div className="text-[10.5px] text-slate-400 italic">
            ARIA reminds you at renewal to confirm each is still on the plan's formulary.
          </div>
        </div>
        <div className="mt-3">
          {rx.length === 0 ? (
            <div className="text-[13px] text-slate-500 italic">None</div>
          ) : (
            <ul className="space-y-1">
              {rx.map((p, i) => (
                <li key={i} className="text-[13px] text-slate-800">
                  <span className="font-semibold">{p.name || '—'}</span>
                  {p.dosage && <span className="text-slate-500 ml-2">· {p.dosage}</span>}
                  {p.generic_ok && <span className="text-emerald-700 ml-2 text-[10.5px] font-bold uppercase tracking-wider">generic OK</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <Label>Conditions</Label>
        <div className="mt-3">
          {conds.length === 0 ? (
            <div className="text-[13px] text-slate-500 italic">None recorded</div>
          ) : (
            <ul className="space-y-1">
              {conds.map((c, i) => (
                <li key={i} className="text-[13px] text-slate-800">
                  <span className="font-semibold">{c.condition || '—'}</span>
                  {c.note && <span className="text-slate-500 ml-2">· {c.note}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   HOUSEHOLD · Coverage Command
   Real data only: household roster (from /api/health/household — resolves
   the household + members joined with client names + HOUSEHOLD-WIDE policies)
   and product-line coverage gaps computed from actual active policies.
   Per-member per-product coverage is NOT relationally tracked, so we never
   fabricate a per-member green/red grid — members show the one real coverage
   fact we have (on the household's primary policy or not).
   ───────────────────────────────────────────────────────────── */
const age = (dob) => {
  if (!dob) return null;
  try {
    const d = new Date(String(dob).slice(0, 10) + 'T00:00:00Z'); const now = new Date();
    let a = now.getUTCFullYear() - d.getUTCFullYear();
    const m = now.getUTCMonth() - d.getUTCMonth();
    if (m < 0 || (m === 0 && now.getUTCDate() < d.getUTCDate())) a--;
    return Number.isFinite(a) ? a : null;
  } catch { return null; }
};

export function HouseholdCoverageMap({ policies, primary, clientName, clientId, ownerId }) {
  const [hh, setHh] = React.useState({ loading: true, members: [], policies: null, error: null });
  React.useEffect(() => {
    if (!clientId) { setHh({ loading: false, members: [], policies: null, error: null }); return; }
    let cancelled = false;
    setHh({ loading: true, members: [], policies: null, error: null });
    authedFetch(`/api/health/household?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) setHh({ loading: false, members: r.data?.members || [], policies: r.data?.policies || null, error: null });
        else setHh({ loading: false, members: [], policies: null, error: r.error || 'load_failed' });
      });
    return () => { cancelled = true; };
  }, [clientId]);

  // Coverage is computed household-wide when we could load the household's
  // policies; otherwise it falls back to the primary client's policies prop.
  const coveragePolicies = hh.policies != null ? hh.policies : (policies || []);
  const covered = {};
  PRODUCTS.forEach((p) => { covered[p.key] = hasProduct(coveragePolicies, p.key); });

  const memberName = (m) => (m.client?.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.relationship_note || 'Household member');
  const roster = [
    { name: clientName || primary?.client_name || 'Client', role: 'Policyholder', dob: primary?.client_dob, onPrimary: true, isSelf: true },
    ...hh.members.map((m) => ({
      name: memberName(m),
      role: (m.role && m.role !== 'other') ? m.role.charAt(0).toUpperCase() + m.role.slice(1) : (m.relationship_note || 'Member'),
      dob: m.dob || m.client?.dob,
      onPrimary: m.on_primary_policy !== false,
      isSelf: false,
    })),
  ];

  const gapCount = PRODUCTS.slice(1).filter((p) => !covered[p.key]).length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between">
        <Label>Household coverage command</Label>
        <div className="text-[10.5px] text-slate-500 italic">coverage tracked per policy, not per person</div>
      </div>

      {/* Roster — real members + the one real per-member fact we have */}
      <div className="mt-4 space-y-2">
        {hh.loading ? (
          <div className="text-[12px] text-slate-400 italic">Loading household…</div>
        ) : roster.map((m, i) => {
          const a = age(m.dob);
          return (
            <div key={i} className="flex items-center gap-2 border border-slate-100 rounded-lg px-3 py-2">
              <span className="font-bold text-[13.5px] text-slate-900">{m.name}</span>
              <span className="text-[11px] text-slate-500">{m.role}{a != null ? ` · age ${a}` : ''}</span>
              {!m.isSelf && (
                <span className={`ml-auto text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${m.onPrimary ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                  {m.onPrimary ? 'On household policy' : 'Separate / own coverage'}
                </span>
              )}
            </div>
          );
        })}
        {!hh.loading && roster.length === 1 && (
          <div className="text-[11.5px] text-slate-400 italic">No household members on file yet — add them in the Household panel below.</div>
        )}
      </div>

      {/* Product-line coverage — computed from ACTUAL active policies */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <Label>Coverage by product line</Label>
          <div className="text-[10.5px] text-slate-500 italic">green = active policy on file · amber = no policy</div>
        </div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1.5">
          {PRODUCTS.map((p) => (
            <div key={p.key} className={`text-[11.5px] font-semibold px-2 py-1 rounded-md border ${
              covered[p.key] ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-700'
            }`}>
              {covered[p.key] ? '✓' : '○'} {p.label}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 text-[11px] text-slate-500 italic">
        {hh.error
          ? 'Coverage shown from this client’s policies (household roster unavailable).'
          : gapCount === 0
            ? 'This household has a policy on file across every product line tracked.'
            : `${gapCount} product line${gapCount === 1 ? '' : 's'} with no policy on file — each is a cross-sell opportunity the AI can turn into a task.`}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   HOUSEHOLD · Caregiver portal (§12)
   ───────────────────────────────────────────────────────────── */
export function CaregiverPortalBlock({ clientId, ownerId }) {
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({});

  React.useEffect(() => {
    if (!clientId || !ownerId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('client_medical_profile')
          .select('caregivers, authorized_representative, poa_type, poa_name, poa_phone, poa_document_url')
          .eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle();
        if (!cancelled) { setProfile(data || {}); setDraft(data || {}); }
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  const caregivers = Array.isArray(profile?.caregivers) ? profile.caregivers : [];

  const save = async () => {
    try {
      const { error } = await supabase.from('client_medical_profile').upsert({
        owner_id: ownerId, client_id: clientId,
        caregivers: draft.caregivers || [],
        authorized_representative: draft.authorized_representative || null,
        poa_type: draft.poa_type || null,
        poa_name: draft.poa_name || null,
        poa_phone: draft.poa_phone || null,
      }, { onConflict: 'owner_id,client_id' });
      if (error) throw error;
      setProfile(draft);
      setEditing(false);
    } catch (e) {
      alert(`Save failed: ${e.message || e}`);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between">
        <Label>Caregiver &amp; authorized rep</Label>
        <button
          onClick={() => { if (editing) save(); else setEditing(true); }}
          className="text-[10.5px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900"
        >
          {editing ? 'Save' : 'Edit'}
        </button>
      </div>
      <div className="mt-1 text-[10.5px] text-slate-400 italic">
        Only records the client has authorized. Authorized rep can call carriers on the client's behalf.
      </div>
      {editing ? (
        <div className="mt-4 space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Authorized rep name</div>
            <input value={draft.authorized_representative || ''} onChange={e => setDraft(d => ({ ...d, authorized_representative: e.target.value }))}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-[13px]"/>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">POA type</div>
              <select value={draft.poa_type || ''} onChange={e => setDraft(d => ({ ...d, poa_type: e.target.value }))}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-[13px]">
                <option value="">—</option>
                <option value="durable">Durable POA</option>
                <option value="medical">Medical POA</option>
                <option value="financial">Financial POA</option>
                <option value="both">Both financial + medical</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">POA name</div>
              <input value={draft.poa_name || ''} onChange={e => setDraft(d => ({ ...d, poa_name: e.target.value }))}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-[13px]"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">POA phone</div>
              <input value={draft.poa_phone || ''} onChange={e => setDraft(d => ({ ...d, poa_phone: e.target.value }))}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-[13px]"/>
            </label>
          </div>
          <div className="text-[10px] text-slate-500 italic">Additional caregiver contacts can be added after this saves.</div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
          <Cell label="Authorized rep" value={profile?.authorized_representative}/>
          <Cell label="POA type" value={profile?.poa_type ? profile.poa_type.replace(/_/g, ' ') : null}/>
          <Cell label="POA contact" value={profile?.poa_name && profile?.poa_phone ? `${profile.poa_name} · ${profile.poa_phone}` : profile?.poa_name || profile?.poa_phone}/>
        </div>
      )}
      {caregivers.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <Label>Additional caregivers on file</Label>
          <div className="mt-2 space-y-1.5">
            {caregivers.map((c, i) => (
              <div key={i} className="text-[13px] text-slate-800 flex items-baseline gap-2 flex-wrap">
                <span className="uppercase tracking-wider text-[10px] font-bold text-slate-500">{c.relation || 'contact'}</span>
                <span className="font-semibold">{c.name || '—'}</span>
                {c.phone && <span className="text-slate-500">· {c.phone}</span>}
                {c.email && <span className="text-slate-500">· {c.email}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   RELATIONSHIP · Personal memory + Who they are
   ───────────────────────────────────────────────────────────── */
export function RelationshipTabView({ clientId, ownerId }) {
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!clientId || !ownerId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('client_medical_profile').select('personal_notes, family_context, interests, preferred_language, preferred_contact_method, best_time_to_call').eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle();
        if (!cancelled) setProfile(data || null);
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  if (loading) return <div className="text-xs text-slate-500 italic px-1 py-2">Loading relationship memory…</div>;

  const family = Array.isArray(profile?.family_context) ? profile.family_context : [];
  const interests = Array.isArray(profile?.interests) ? profile.interests : [];
  const spouse = family.find(f => /spouse|partner/i.test(f.relation || ''));
  const kids = family.filter(f => /child|son|daughter|kid/i.test(f.relation || ''));
  const occupation = family.find(f => /occupation|work|job/i.test(f.relation || ''))?.note || '';
  const military = family.find(f => /military|vet|army|navy|marine|air force/i.test(f.relation || ''))?.note || '';

  return (
    <div className="space-y-3">
      {profile?.personal_notes && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-[11.5px] font-bold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-1.5 mb-2">
            <span className="text-[#e8a020]">✦</span>
            <span>Relationship memory</span>
          </div>
          <div className="text-[13.5px] leading-relaxed text-slate-800 whitespace-pre-wrap">
            {profile.personal_notes}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <Label>Who they are</Label>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
          <Cell label="Spouse / partner" value={spouse ? spouse.name || spouse.note : null}/>
          <Cell label="Children" value={kids.length === 0 ? null : kids.map(k => k.name || k.note).filter(Boolean).join(', ')}/>
          <Cell label="Occupation" value={occupation}/>
          <Cell label="Hobbies" value={interests.length === 0 ? null : interests.join(', ')}/>
          <Cell label="Military service" value={military}/>
          <Cell label="Prefers" value={profile?.preferred_contact_method && profile?.best_time_to_call ? `${profile.preferred_contact_method} · ${profile.best_time_to_call}` : profile?.preferred_contact_method || profile?.best_time_to_call || null}/>
        </div>
      </div>

      {family.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <Label>Family context (raw)</Label>
          <div className="mt-3 space-y-1">
            {family.map((f, i) => (
              <div key={i} className="text-[12.5px] text-slate-800">
                <span className="uppercase tracking-wider text-slate-500 text-[10px] font-bold">{f.relation || 'note'}:</span>
                <span className="ml-2 font-semibold">{f.name || ''}</span>
                {f.note && <span className="text-slate-600 ml-2">{f.note}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   COMPLIANCE · 7-item checklist + submission-ready banner
   ───────────────────────────────────────────────────────────── */
export function ComplianceChecklistBlock({ clientId }) {
  const [packet, setPacket] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const p = await apiFetch(`/api/audit/packet?client_id=${encodeURIComponent(clientId)}`);
        if (cancelled) return;
        if (p?.error) throw new Error(p.error);
        setPacket(p);
      } catch (e) { setErr(e.message || String(e)); } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) return <div className="text-xs text-slate-500 italic px-1 py-2">Loading compliance state…</div>;
  if (err)     return <div className="text-xs text-rose-700 italic px-1 py-2">Compliance packet failed: {err}</div>;
  if (!packet) return null;

  const events = packet.events || [];
  const forms = packet.forms || [];
  const checks = [
    { key: 'consent',        label: 'Consent to contact recorded',    done: events.some(e => /consent|hipaa|permission/i.test(e.event_type || '')) },
    { key: 'sms',            label: 'Permission to text',             done: events.some(e => /sms|text/i.test(e.event_type || '')) },
    { key: 'email',          label: 'Permission to email',            done: events.some(e => /email/i.test(e.event_type || '')) },
    { key: 'marketplace',    label: 'Marketplace disclosures completed', done: (packet.checklists || []).some(c => c.items) },
    { key: 'signature',      label: 'Signature captured',             done: forms.some(f => (f.signatures?.client || f.signatures?.agent)) },
    { key: 'call',           label: 'Call recorded / stored',         done: events.some(e => /call.*record|recording/i.test(e.event_type || '')) },
    { key: 'docs',           label: 'Required documents uploaded',    done: events.some(e => /doc|upload/i.test(e.event_type || '')) || forms.some(f => f.certificate_url) },
  ];
  const doneCount = checks.filter(c => c.done).length;
  const pct = Math.round((doneCount / checks.length) * 100);
  const ready = doneCount === checks.length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between">
        <Label>Compliance checklist</Label>
        <div className={`text-[12px] font-bold ${ready ? 'text-emerald-700' : 'text-slate-600'}`}>{pct}% complete</div>
      </div>
      <div className="mt-4 space-y-1.5">
        {checks.map(c => (
          <div key={c.key} className="flex items-center gap-3">
            <div className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[13px] font-bold ${c.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
              {c.done ? '✓' : ''}
            </div>
            <div className={`flex-1 text-[13.5px] ${c.done ? 'text-slate-800' : 'text-slate-600'}`}>{c.label}</div>
            <div className="shrink-0 text-[10.5px] uppercase tracking-widest text-slate-400 font-bold">{c.done ? 'recorded' : 'pending'}</div>
          </div>
        ))}
      </div>
      <div className={`mt-4 border rounded-lg px-3 py-2 text-[12.5px] ${ready ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
        {ready ? '✓ This record is submission-ready.' : `${checks.length - doneCount} item${checks.length - doneCount === 1 ? '' : 's'} still needed before submission.`}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   DOCUMENTS · Simple 4-item checklist matching demo
   ───────────────────────────────────────────────────────────── */
export function DocumentChecklistBlock({ clientId, ownerId }) {
  const [docs, setDocs] = React.useState([]);
  React.useEffect(() => {
    if (!clientId || !ownerId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('client_documents').select('doc_type, filename').eq('owner_id', ownerId).eq('client_id', clientId);
        if (!cancelled) setDocs(data || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  const has = (type) => docs.some(d => d.doc_type === type);
  const checklist = [
    { type: 'application',        label: 'Application' },
    { type: 'proof_of_income',    label: 'Proof of income' },
    { type: 'drivers_license',    label: 'ID verification' },
    { type: 'marketplace_document', label: 'Disclosures signed' },
  ];
  const allDone = checklist.every(c => has(c.type));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <Label>Document checklist</Label>
      <div className="mt-4 space-y-1.5">
        {checklist.map(c => {
          const done = has(c.type);
          return (
            <div key={c.type} className="flex items-center gap-3">
              <div className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[13px] font-bold ${done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {done ? '✓' : ''}
              </div>
              <div className={`flex-1 text-[13.5px] ${done ? 'text-slate-800' : 'text-slate-600'}`}>{c.label}</div>
              <div className="shrink-0 text-[10.5px] uppercase tracking-widest text-slate-400 font-bold">{done ? 'uploaded' : 'missing'}</div>
            </div>
          );
        })}
      </div>
      <div className={`mt-4 border rounded-lg px-3 py-2 text-[12.5px] ${allDone ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
        {allDone ? '✓ All required documents on file.' : `${checklist.filter(c => !has(c.type)).length} document${checklist.filter(c => !has(c.type)).length === 1 ? '' : 's'} still missing.`}
      </div>
    </div>
  );
}

/* Provider Review AI — small callout inside the Medical tab.
 *
 * Real network verification requires a carrier provider-directory
 * feed the platform doesn't have yet. Until that's wired, we
 * surface the count of providers on file + last-checked stamp +
 * the plan the check is against, and leave a "verify now" hook.
 * The message is honest ("last verified") — no fake ✓/× marks. */
function ProviderNetworkStatus({ profile }) {
  const primary = profile?.primary_doctor?.trim();
  const hospital = profile?.preferred_hospital?.trim();
  const specialists = Array.isArray(profile?.specialists) ? profile.specialists : [];
  const totalProviders =
    (primary ? 1 : 0) + (hospital ? 1 : 0) + specialists.filter(s => s?.name).length;
  const lastChecked = profile?.provider_network_verified_at;

  if (totalProviders === 0) return null;

  return (
    <div className="mt-4 border-t border-slate-100 pt-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="text-[11.5px] text-slate-600">
        <span className="font-bold text-slate-800">{totalProviders} provider{totalProviders === 1 ? '' : 's'} on file.</span>
        {' '}
        {lastChecked
          ? `Last network check ${new Date(lastChecked).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.`
          : 'No network verification recorded yet.'}
      </div>
      <button
        className="text-[10.5px] font-bold uppercase tracking-wider text-slate-600 border border-slate-300 rounded-md px-2 py-1 opacity-50 cursor-not-allowed"
        title="Carrier network feed pending — surface enabled once provider-directory API is wired"
        disabled
      >
        Verify network (soon)
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   TIMELINE · Clean dot / date / description list (demo shape)
   ───────────────────────────────────────────────────────────── */
export function HealthTimelineBlock({ clientId, ownerId }) {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const out = [];
      // Single source of truth: /api/clients/timeline aggregates calls, TEXT
      // MESSAGES (matched by the client's phone, not just client_id), EMAIL
      // history, appointments, policy lifecycle, and audit events — with the
      // actual message body / email subject, so this reads as real history.
      try {
        const j = await apiFetch(`/api/clients/timeline?client_id=${encodeURIComponent(clientId)}`);
        for (const e of (j?.events || [])) {
          const d = e.detail || {};
          let text = e.summary || '';
          if (e.kind === 'message') {
            const body = (d.body || '').replace(/\s+/g, ' ').trim();
            text = `${e.direction === 'in' ? 'Received text' : 'Texted'} — ${body || '(no text)'}`;
          } else if (e.kind === 'email') {
            text = `${e.direction === 'in' ? 'Received email' : 'Emailed'} — ${d.subject || '(no subject)'}`;
          } else if (e.kind === 'policy' || e.kind === 'enroll') {
            text = e.summary || 'Policy event';
          }
          out.push({ ts: e.at, kind: e.kind, text });
        }
      } catch {}
      out.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
      if (!cancelled) { setEvents(out.slice(0, 60)); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  const dotClass = (kind) => kind === 'enroll' || kind === 'policy' ? 'bg-emerald-500'
                          : kind === 'message' ? 'bg-sky-500'
                          : kind === 'email'   ? 'bg-indigo-500'
                          : kind === 'call'    ? 'bg-violet-500'
                          : kind === 'audit'   ? 'bg-amber-500'
                          :                      'bg-slate-400';

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <Label>Client health timeline</Label>
      {loading ? (
        <div className="text-[12px] text-slate-500 italic mt-3">Loading timeline…</div>
      ) : events.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic mt-3">No events yet on this client.</div>
      ) : (
        <ul className="mt-4 space-y-3">
          {events.map((e, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className={`shrink-0 w-2.5 h-2.5 rounded-full mt-1.5 ${dotClass(e.kind)}`}/>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 font-mono">
                  {e.ts ? new Date(e.ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </div>
                <div className="text-[13.5px] text-slate-800 mt-0.5">{e.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CROSS-SELL · Dollar-per-year gaps with Create task
   ───────────────────────────────────────────────────────────── */
export function CrossSellOpportunitiesBlock({ policies, showToast = () => {} }) {
  const gaps = PRODUCTS.slice(1).filter(p => !hasProduct(policies, p.key));
  const totalAnnual = gaps.reduce((s, g) => s + g.annual, 0);

  const createTask = (product) => {
    showToast(`Task created — Offer ${product.label} (${product.product})`, 'ok');
    // TODO(follow-up): persist to a tasks table; today this fires a
    // toast so the operator has feedback that the click was captured.
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between">
        <div className="text-[11.5px] font-bold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-1.5">
          <span className="text-[#e8a020]">✦</span>
          <span>Cross-sell opportunities</span>
        </div>
      </div>
      <div className="mt-3 text-[13.5px] text-slate-700">
        {gaps.length === 0
          ? 'No coverage gaps — this client is fully protected.'
          : (
            <>
              This client has <strong>{gaps.length}</strong> coverage gap{gaps.length === 1 ? '' : 's'} worth about <strong className="text-emerald-700">${totalAnnual.toLocaleString()}/yr</strong> in premium. Lead with the highest-value gap below.
            </>
          )}
      </div>
      {gaps.length > 0 && (
        <div className="mt-4 divide-y divide-slate-100">
          {gaps.sort((a, b) => b.annual - a.annual).map(g => (
            <div key={g.key} className="py-3 flex items-center gap-3">
              <div className="shrink-0 w-8 h-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold">◆</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-bold text-slate-900 truncate">{g.label}</div>
                <div className="text-[11.5px] text-slate-500 mt-0.5 truncate">{g.product}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[13px] font-black tabular-nums text-emerald-700">${g.annual}</div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-400">/ yr</div>
              </div>
              <button
                onClick={() => createTask(g)}
                className="shrink-0 text-[11px] font-bold uppercase tracking-wider bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-1.5"
              >
                Create task
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
